import { devLog } from './devLog'
import { getUrlParam, removeUrlParam } from './urlParams'
import { getFirstPathSegment } from './urlPath'

// After the user authorises in their bank, GoCardless sends them back to
// `/gocardless-return` — often in a different browser than the one that started
// the flow (an in-app browser hands off to the system browser), which therefore
// has no `gocardless-session` cookie. The backend puts a short-lived signed
// token in the return URL and the return endpoints accept it as a fallback for
// the cookie.
//
// The token wins over the cookie there, not the other way round: it names the
// transaction the bank redirected for, while the cookie only says what this
// browser last did — a system browser can hold a valid session for an unrelated
// GoCardless transaction. So a token the backend cannot read is rejected even
// when a cookie is present, and the cookie stands in only when no token was
// sent, or when the one sent merely expired for that same transaction. Sending
// both is therefore safe only as long as the token belongs to the return we are
// on, which is why it is captured on that page alone.
//
// It is also kept no longer than it is needed: `complete` re-issues the cookie
// in this browser, so the first response that comes back authorized makes the
// stored copy redundant and the page drops it. What is stored survives only an
// unanswered first call — a reload before it, which still has to work — and the
// tab leaving the return page. sc-21124

const STORAGE_KEY = 'gocardlessSessionToken'
const SESSION_TOKEN_PARAM = 'sessionToken'
const RETURN_PATH_SEGMENT = 'gocardless-return'

// Fallback for browsers where session storage throws (private mode, blocked
// site data). It covers the current page load; only a refresh would lose it.
let inMemoryToken: string | null = null

export const getGocardlessSessionToken = (): string | null => {
  if (inMemoryToken) return inMemoryToken

  try {
    return sessionStorage.getItem(STORAGE_KEY)
  } catch (e) {
    devLog('Unable to read GoCardless session token from session storage')
    devLog(e)
    return null
  }
}

export const forgetGocardlessSessionToken = (): void => {
  inMemoryToken = null

  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    devLog('Unable to clear GoCardless session token from session storage')
    devLog(e)
  }
}

export const captureGocardlessSessionToken = (): void => {
  // The backend reuses the `sessionToken` parameter name for the onboarding
  // session token it puts on `/onboarding/continue`. The two are not
  // interchangeable — each is accepted only on its own endpoints and would get
  // a 401 on the other's — so capture ours on the return page and nowhere else,
  // to make sure the onboarding one can never overwrite it.
  //
  // Landing anywhere else means the tab left the return page for good — the
  // flow moved on, or a new one started. Drop the token there, so a later
  // return can never be authorized with a finished transaction's one: the
  // return endpoints trust it without cross-checking the transaction.
  if (getFirstPathSegment() !== RETURN_PATH_SEGMENT) {
    forgetGocardlessSessionToken()
    return
  }

  const token = getUrlParam(SESSION_TOKEN_PARAM)

  // The return page without a token in the URL is a reload of the page we
  // already sanitized on its first load, so keep what was stored back then —
  // refreshes and retries have to survive, that is the whole point of the fix.
  if (!token) return

  inMemoryToken = token

  try {
    sessionStorage.setItem(STORAGE_KEY, token)
  } catch (e) {
    devLog('Unable to persist GoCardless session token in session storage')
    devLog(e)
  }

  // Strip the token from the visible URL only once it is stored, so it does not
  // leak through `Referer`, browser history or screenshots.
  removeUrlParam(SESSION_TOKEN_PARAM)
}

export const withGocardlessSessionToken = (url: string): string => {
  const token = getGocardlessSessionToken()

  if (!token) return url

  const separator = url.includes('?') ? '&' : '?'

  return `${url}${separator}${SESSION_TOKEN_PARAM}=${encodeURIComponent(token)}`
}
